import {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {InfinityScroll} from '../../design-system/atoms/InfinityScroll/InfinityScroll';
import {Loading} from '../../design-system/atoms/Loading/Loading';
import {
  StaticLandingGenreSection,
  StaticLandingGenreSectionQueryVariables,
} from '../../graphql/generated/types';
import {onSuccessStaticLandingSection} from '../../pagewebLayout/PagewebLayoutSlice';
import {CACHE_ONLY_CONFIG} from '../../reactQuery/constant';
import {useInfiniteStaticLandingGenreSectionQuery} from '../../wrapper/landingWrapper';
import useTypedSelector from '../useTypedSelector';
import {StaticLandingGenreMoreFetcherProps} from './StaticLandingGenreMoreFetcherProps.interface';

export function StaticLandingGenreMoreFetcher({
  children,
  section,
}: StaticLandingGenreMoreFetcherProps) {
  const landingGenreSection = useTypedSelector(
    state =>
      state.pagewebLayout.entities.sections[section.id] as Omit<
        StaticLandingGenreSection,
        'groups'
      >
  );
  const {param: currentParam} = landingGenreSection;
  const param: StaticLandingGenreSectionQueryVariables['param'] = {
    categoryUid: currentParam.categoryUid,
    subcategoryUid: currentParam.subcategory?.param,
    sortType: currentParam.sortType?.param,
    isComplete: !!currentParam.isComplete,
    screenUid: currentParam.screenUid,
  };

  const {fetchNextPage, isFetching, isFetchedAfterMount, isSuccess, data} =
    useInfiniteStaticLandingGenreSectionQuery(
      {
        sectionId: section.id,
        param,
      },
      {
        initialPageParam: {sectionId: section.id, param},
        getNextPageParam: lastPage => {
          const section =
            lastPage.staticLandingGenreSection as StaticLandingGenreSection;
          return section.isEnd
            ? undefined
            : {
                param: {
                  ...param,
                  page: (section.param.page || 0) + 1,
                },
                sectionId: section.id,
              };
        },
        initialData: landingGenreSection
          ? {
              pages: [
                {
                  staticLandingGenreSection:
                    landingGenreSection as StaticLandingGenreSection,
                },
              ],
              pageParams: [{sectionId: section.id, param}],
            }
          : undefined,
        ...CACHE_ONLY_CONFIG,
      }
    );

  const dispatch = useDispatch();
  const lastPage = data?.pages[data.pages.length - 1]
    ?.staticLandingGenreSection as StaticLandingGenreSection | undefined;
  useEffect(() => {
    if (isFetchedAfterMount && isSuccess) {
      dispatch(
        onSuccessStaticLandingSection(lastPage, {
          sectionId: section.id,
          page: lastPage?.param.page || 0,
        })
      );
    }
  }, [dispatch, isFetchedAfterMount, isSuccess, lastPage, section.id]);

  return (
    <>
      <InfinityScroll
        isLoading={isFetching}
        canLoadNextPage={!isFetching && !lastPage?.isEnd}
        requestNextPageData={fetchNextPage}
      >
        {children}
      </InfinityScroll>
      {isFetching && (
        <div className="flex-center-full">
          <Loading />
        </div>
      )}
    </>
  );
}
